import { Heading } from 'blaze-ui/Heading/Heading'
import Styles from './BrandKitPreviewCard.module.scss'
import { CSSProperties, useMemo, useRef } from 'react'
import { Text } from 'blaze-ui/Text/Text'
import { first } from 'lodash'
import { BrandKitColorCardProps } from '../Types'
import { GoogleFontLoader } from 'components/GoogleFontLoader/GoogleFontLoader'
import { ResponsiveDesign } from 'screens/blaze/Home/components/ResponsiveDesign'
import { BrandKitLogo } from './BrandKitLogo'
import cx from 'classnames'
import { BlazeFontLoader } from 'components/BlazeFontLoader/BlazeFontLoader'

export const BrandKitPreviewCard = ({
  colors,
  fontPairing,
  brandKit,
  isLogoReadOnly,
  onLogoChange,
  bodyClassName,
  headerClassName,
  descriptionLineClamp,
}: BrandKitColorCardProps) => {
  const nameRef = useRef<HTMLHeadingElement>(null)

  const bannerUrl = useMemo(() => {
    return brandKit.media_files[0]?.source_url
  }, [brandKit.media_files])

  // Extract the functional colors (bg, accent, text) from the display palette

  // Use the functional colors for the card display
  const bgColor = first(colors)
  const separatorColor = colors[1] // Accent color
  const fontColor = colors[2] || colors[1] // Fallback to accent color if no font color is available

  const nameHeight = nameRef.current?.clientHeight
  let nameLines = 1
  if (nameHeight && nameHeight > 50) nameLines = 2
  if (nameHeight && nameHeight > 95) nameLines = 3

  let textLineClamp = descriptionLineClamp || (nameLines === 1 ? 5 : 3)
  if (!descriptionLineClamp && nameLines > 2) textLineClamp = 2

  return (
    <div
      data-testid="brand-kit-preview-card"
      className={Styles.brandKitColorCard}
      style={
        {
          '--title-font-family': `"${fontPairing[0]?.style.font_family}"`,
          '--title-font-weight': fontPairing[0]?.style.font_weight,
          '--title-line-height': fontPairing[0]?.style.line_height,
          '--title-letter-spacing': `${(fontPairing[0]?.style.letter_spacing || 0) / 1000}em`,
          '--body-font-family': `"${fontPairing[1]?.style.font_family}"`,
          '--body-font-weight': fontPairing[1]?.style.font_weight,
          '--body-line-height': fontPairing[1]?.style.line_height,
          '--body-letter-spacing': `${(fontPairing[1]?.style.letter_spacing || 0) / 1000}em`,
          '--banner-url': `url(${bannerUrl})`,
        } as CSSProperties
      }
    >
      {brandKit.brand_visual_kit?.id && (
        <BrandKitLogo
          brandVisualKitId={brandKit.brand_visual_kit?.id}
          initialLogo={brandKit.brand_visual_kit?.primary_logo}
          backgroundColor={fontColor}
          isReadOnly={isLogoReadOnly}
          onLogoChange={onLogoChange}
          className={headerClassName}
        />
      )}
      <div className={cx(Styles.nameAndColors, bodyClassName)} style={{ backgroundColor: bgColor }}>
        <Heading color={fontColor} level={1} lineClamp={2} ref={nameRef}>
          {brandKit.name}
        </Heading>
        <div className={Styles.separator} style={{ backgroundColor: separatorColor }}></div>
        <Text lineClamp={textLineClamp} color={fontColor}>
          {brandKit.brand_voice?.voice_params?.purpose?.join(', ') || 'Lorem ipsum dolor sit amet'}
        </Text>
      </div>
      <ResponsiveDesign.Mobile>
        <GoogleFontLoader family={fontPairing[0]?.style.font_family} />
        <BlazeFontLoader name={fontPairing[0]?.style.font_family} />
        <GoogleFontLoader family={fontPairing[1]?.style.font_family} />
        <BlazeFontLoader name={fontPairing[1]?.style.font_family} />
      </ResponsiveDesign.Mobile>
    </div>
  )
}
